'use client'
import { Cart } from '@/components/Cart'
import { OpenCartButton } from '@/components/Cart/OpenCart'
import { Link } from '@/i18n/routing'
import React, { Suspense, useState } from 'react'
import { SearchIcon, XIcon, UserIcon, LogOutIcon } from 'lucide-react'
import { Search } from '@/components/Search'
import { useAuth } from '@/providers/Auth'
import { LanguageToggle } from '@/components/LanguageToggle'
import { useTranslations } from 'next-intl'
import { AnnouncementBanner } from '@/components/AnnouncementBanner'
import { WishlistBadgeClient } from './WishlistBadgeClient'

import dynamic from 'next/dynamic'

const MobileMenu = dynamic(
  () => import('./MobileMenu').then((m) => ({ default: m.MobileMenu })),
  {
    ssr: false,
    loading: () => <div className="w-10 h-10" aria-hidden="true" />,
  },
)
import type { Header } from 'src/payload-types'

import { HeaderNav } from './HeaderNav'
import { AdminUserStats } from './AdminUserStats'
import Image from 'next/image'
import { useLocale } from 'next-intl'

type Props = {
  header: Header
  initialWishlistCount?: number
  logo: string
  brandName: string
  wishlistEnabled: boolean
}

export function HeaderClient({
  header,
  initialWishlistCount = 0,
  logo,
  brandName,
  wishlistEnabled,
}: Props) {
  const t = useTranslations('common')
  const navT = useTranslations('navigation')
  const locale = useLocale()
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const { user, logout } = useAuth()
  const menu = header.navItems || []

  const isAdmin = user?.roles && Array.isArray(user.roles) && user.roles.includes('admin')

  return (
    <>
      {/* The refraction lens used by `.headerGlassRefract` (index.css). feTurbulence
          builds a noise field, blurred, then feDisplacementMap bends the backdrop
          through it — real glass refraction, ported from the design prototype's
          #lgRefract (stammbaum-v5). Rendered once; hidden. Chromium reads it via
          backdrop-filter: url(); other browsers ignore it and keep plain blur. */}
      <svg aria-hidden width="0" height="0" style={{ position: 'absolute' }}>
        <filter
          id="lgRefract"
          x="-25%"
          y="-25%"
          width="150%"
          height="150%"
          colorInterpolationFilters="sRGB"
        >
          <feTurbulence
            type="fractalNoise"
            baseFrequency="0.006 0.010"
            numOctaves={2}
            seed={9}
            result="t"
          />
          <feGaussianBlur in="t" stdDeviation={7} result="ts" />
          <feDisplacementMap
            in="SourceGraphic"
            in2="ts"
            scale={18}
            xChannelSelector="R"
            yChannelSelector="G"
          />
        </filter>
      </svg>
      <header className="sticky top-0 z-50 w-full flex flex-col headerSafeArea">
        {/* Utility bar — GeistMono stays ONLY here (D-1). No backdrop-blur: a thin
            strip does not need to spend a blur element; a token fill is enough. */}
        <div className="w-full bg-[var(--glass-fill-sidebar)] border-b border-border px-2 sm:px-4 lg:px-8 2xl:px-12">
          <div
            className="flex items-center justify-end gap-3 h-7 text-micro text-muted-foreground"
            style={{ fontFamily: 'var(--font-geist-mono), monospace' }}
          >
            {/* Language group */}
            <div className="flex items-center flex-shrink-0">
              <LanguageToggle />
            </div>

            <div className="w-px h-3 bg-border flex-shrink-0" aria-hidden="true" />

            {user ? (
              <>
                {/* Admin group */}
                {isAdmin && (
                  <div className="hidden md:flex items-center gap-3">
                    <a
                      href="/admin"
                      className="text-jade hover:text-foreground transition-colors tracking-widest uppercase flex items-center gap-1"
                    >
                      Dashboard
                    </a>
                    <div className="w-px h-3 bg-border flex-shrink-0" aria-hidden="true" />
                    <AdminUserStats />
                    <div className="w-px h-3 bg-border flex-shrink-0" aria-hidden="true" />
                  </div>
                )}

                {/* Account group */}
                <div className="flex items-center gap-3">
                  <span className="truncate max-w-[200px] hidden sm:inline">{user.email}</span>
                  <Link href="/account" className="hover:text-jade transition-colors">
                    {navT('account')}
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="flex items-center gap-1 hover:text-jade transition-colors"
                  >
                    <LogOutIcon className="w-3 h-3" />
                    {navT('logout')}
                  </button>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link href="/login" className="hover:text-jade transition-colors">
                  {navT('login', { fallback: 'Login' })}
                </Link>
                <span className="opacity-30">|</span>
                <Link href="/create-account" className="hover:text-jade transition-colors">
                  {navT('register', { fallback: 'Register' })}
                </Link>
              </div>
            )}
          </div>
        </div>

        <div
          className="headerGlass headerGlassRefract glassGlareHost w-full border-b border-border shadow-lg relative"
          onPointerMove={(e) => {
            const r = e.currentTarget.getBoundingClientRect()
            e.currentTarget.style.setProperty('--mx', `${((e.clientX - r.left) / r.width) * 100}%`)
            e.currentTarget.style.setProperty('--my', `${((e.clientY - r.top) / r.height) * 100}%`)
          }}
        >
          {/* Pointer-tracking glass glare — see index.css `.glassGlare` */}
          <span aria-hidden className="glassGlare" />
          <nav className="px-2 sm:px-4 lg:px-8 2xl:px-12 flex items-center justify-between h-20">
            {/* 1. Left: Hamburger menu icon */}
            <div className="lg:hidden flex-shrink-0 min-w-[40px] flex items-center">
              <Suspense fallback={null}>
                <MobileMenu menu={menu} brandName={brandName} />
              </Suspense>
            </div>

            {/* 2. Center: Logo. White PNG — invert to ink in light mode, keep white
                in dark (same pattern as the footer). It deliberately overflows the
                80px bar at lg — that is the brand moment (§5). */}
            <div className="flex-1 lg:flex-none flex justify-center lg:justify-start items-center h-full overflow-hidden text-center lg:overflow-visible lg:mr-6">
              <Link
                href="/"
                className="relative flex justify-center lg:justify-start items-center h-full w-full max-w-[160px] sm:max-w-[200px] lg:max-w-none lg:w-48 lg:-ml-6"
              >
                <Image
                  src={logo}
                  alt={`${brandName} Logo`}
                  width={400}
                  height={120}
                  className="h-16 sm:h-20 lg:h-36 w-auto transition-all object-contain invert dark:invert-0 lg:absolute lg:left-0 lg:top-1/2 lg:-translate-y-1/2 lg:max-w-none"
                  priority
                />
              </Link>
            </div>

            {/* Center: Navigation or Search. Centred with auto margins (inset-x-0
                + mx-auto + w-max) rather than a transform — a `transform` here
                would become the containing block for Radix's fixed mega-panel and
                anchor it off-centre, running the panel off the right edge on normal
                widths. No blur on the transition wrappers either, for the same
                reason (a filter also creates a containing block). With both gone the
                full-width header bar is the panel's reference, so it centres on the
                viewport. */}
            <div className="hidden lg:flex absolute inset-x-0 mx-auto w-max h-full items-center">
              {/* Navigation - hidden when search is open */}
              <div
                className={`transition-all duration-300 ease-out ${
                  isSearchOpen ? 'opacity-0 pointer-events-none absolute' : 'opacity-100'
                }`}
              >
                <HeaderNav header={header} />
              </div>

              {/* Search - shown when search is open */}
              <div
                className={`flex items-center gap-4 transition-all duration-300 ease-out ${
                  isSearchOpen
                    ? 'opacity-100 w-[600px]'
                    : 'opacity-0 pointer-events-none absolute w-0'
                }`}
              >
                <Search className="flex-1" onSelect={() => setIsSearchOpen(false)} />
                <button
                  onClick={() => setIsSearchOpen(false)}
                  className="flex h-11 w-11 items-center justify-center hover:bg-foreground/10 rounded-full transition-all hover:rotate-90 duration-300 group flex-shrink-0"
                  aria-label={t('close')}
                >
                  <XIcon className="h-5 w-5 text-foreground group-hover:text-jade" />
                </button>
              </div>
            </div>

            {/* 3. Right: A group of icons */}
            <div className="flex-shrink-0 flex justify-end items-center gap-3 sm:gap-5 shrink-0">
              {wishlistEnabled && (
                <WishlistBadgeClient locale={locale} initialCount={initialWishlistCount} />
              )}

              {/* Search toggle button — wide-desktop only. Below 2xl the MobileMenu owns
                  search (see MobileMenu.tsx); the icon was a dead no-op there. Search
                  panel needs ~600px of horizontal real estate (see below), so it stays
                  at 2xl even though the rest of the header switches to lg at HDR-2/6. */}
              <button
                onClick={() => setIsSearchOpen(!isSearchOpen)}
                className="hidden 2xl:inline-flex h-11 w-11 items-center justify-center hover:bg-foreground/10 rounded-full transition-all hover:scale-110 duration-200 group flex-shrink-0"
                aria-label={isSearchOpen ? t('close') : t('search')}
              >
                {isSearchOpen ? (
                  <>
                    <XIcon className="h-4 w-4 sm:h-5 sm:w-5 text-foreground group-hover:text-jade 2xl:hidden" />
                    {/* Invisible icon on desktop to preserve width and prevent layout shift */}
                    <SearchIcon className="hidden 2xl:block h-5 w-5 opacity-0" />
                  </>
                ) : (
                  <SearchIcon className="h-4 w-4 sm:h-5 sm:w-5 text-foreground group-hover:text-jade" />
                )}
              </button>

              <Suspense fallback={<OpenCartButton />}>
                <Cart />
              </Suspense>

              <Link
                href={user ? '/account' : '/login'}
                className="h-11 w-11 flex items-center justify-center rounded-full transition-all hover:bg-foreground/10 hover:scale-110 duration-200 group flex-shrink-0"
                aria-label={navT('account')}
              >
                <UserIcon className="h-4 w-4 sm:h-5 sm:w-5 text-foreground group-hover:text-jade transition-colors" />
              </Link>
            </div>
          </nav>
        </div>
      </header>
      <AnnouncementBanner announcement={header.announcement} />
    </>
  )
}
